<template>
  <div class="input-container text-start">
    <label v-if="label" class="form-label">{{ label }}</label>

    <div class="input-group mb-0 bg-light-subtle rounded-3 align-items-center">
      <span class="input-group-text text-muted" v-if="icon">
        <i :class="icon"></i>
      </span>

      <!-- FILE -->
      <template v-if="type === 'file'">
        <input ref="fileInput" class="form-control form-control-lg border-light bg-light-subtle" type="file"
          :accept="accept || null" :multiple="multiple" @change="onFileChange" :required="required" />
      </template>

      <!-- DATE -->
      <template v-else-if="type === 'date'">
        <input ref="dateInput" class="form-control form-control-lg border-light bg-light-subtle" type="date"
          :placeholder="placeHolder" :value="isoValue" @input="onDateInput" :required="required" :min="minIso || null"
          :max="maxIso || null" />
        <span v-if="clearable && isoValue" class="input-group-text cursor-pointer" @click="clearDate">
          <i class="ri-close-line"></i>
        </span>
      </template>

      <!-- GENERIC (text, password, email, number, tel, url, etc.) -->
      <template v-else>
        <input class="form-control form-control-lg border-light bg-light-subtle" :type="inputType" :autocomplete="autocomplete"
          :placeholder="placeHolder" :value="modelValue" @input="$emit('update:modelValue', $event.target.value)"
          :required="required" :disabled="disabled" />

        <span v-if="type === 'password'" @click="mudarVisibilidadeSenha()" class="input-group-text cursor-pointer">
          <i :class="classeSenhaVisibilidade()"></i>
        </span>
      </template>
    </div>

    <!-- nomes de arquivos selecionados -->
    <div v-if="type === 'file' && showFileNames && selectedFileNames.length" class="mt-1">
      <small class="text-muted">{{ selectedFileNames.join(', ') }}</small>
    </div>

    <!-- help text comum (date/text/file) -->
    <small v-if="helpText" class="text-muted d-block mt-1">{{ helpText }}</small>
  </div>
</template>

<script>
export default {
  name: 'InputField',
  props: {
    autocomplete: { type: String, default: 'on' }, // as PropType<'on'|'off'|'new-password'>
    label: { type: String },
    type: {
      type: String,
      default: 'text',
      validator: (value) => {
        return ['text', 'password', 'email', 'number', 'tel', 'url', 'file', 'date'].includes(value)
      }
    },
    icon: { type: String },
    placeHolder: { type: String, default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // aceita String/Number/Object(Array/File)/Date (Date é tipo Object)
    modelValue: { type: [String, Number, Object, Array], default: '' },

    /* file props */
    accept: { type: String, default: null }, // ex: "image/*,.pdf"
    multiple: { type: Boolean, default: false },
    showFileNames: { type: Boolean, default: true },

    /* date props */
    emitFormat: {
      type: String,
      default: 'iso', // 'iso' | 'br' | 'date'
      validator: v => ['iso', 'br', 'date'].includes(v)
    },
    min: { type: [String, Date, null], default: null },
    max: { type: [String, Date, null], default: null },
    clearable: { type: Boolean, default: true },

    /* help text */
    helpText: { type: String, default: '' }
  },
  data() {
    return {
      senhaVisivel: false,
      selectedFileNames: []
    }
  },
  computed: {
    inputType() {
      if (this.type === 'password') {
        return this.senhaVisivel ? 'text' : 'password'
      }
      return this.type
    },
    // para date: valor em YYYY-MM-DD para alimentar o input[type=date]
    isoValue() {
      return this.toIsoFromModel(this.modelValue) || ''
    },
    minIso() {
      return this.normalizeToIso(this.min)
    },
    maxIso() {
      return this.normalizeToIso(this.max)
    }
  },
  watch: {
    modelValue() {
      // se quiser reação extra ao modelValue (ex.: atualizar altura auto-grow em textarea), faça aqui
    }
  },
  methods: {
    /* password */
    mudarVisibilidadeSenha() {
      this.senhaVisivel = !this.senhaVisivel
    },
    classeSenhaVisibilidade() {
      return (this.senhaVisivel) ? 'ri-eye-off-line' : 'ri-eye-line'
    },

    /* file handling */
    onFileChange(e) {
      const files = e.target.files
      if (!files || files.length === 0) {
        this.selectedFileNames = []
        this.$emit('update:modelValue', this.multiple ? [] : null)
        return
      }
      this.selectedFileNames = Array.from(files).map(f => f.name)
      if (this.multiple) {
        this.$emit('update:modelValue', Array.from(files))
      } else {
        this.$emit('update:modelValue', files[0])
      }
    },
    clearFile() {
      if (this.$refs.fileInput) this.$refs.fileInput.value = null
      this.selectedFileNames = []
      this.$emit('update:modelValue', this.multiple ? [] : null)
    },

    /* date handling */
    onDateInput(e) {
      const iso = e.target.value || null
      this.emitFromIso(iso)
    },
    clearDate() {
      if (this.$refs.dateInput) this.$refs.dateInput.value = ''
      this.$emit('update:modelValue', null)
    },

    // converte modelValue (Date | ISO string | BR string) -> YYYY-MM-DD | null
    toIsoFromModel(val) {
      if (val === null || val === undefined || val === '') return null
      // Date
      if (val instanceof Date && !isNaN(val)) {
        return this.dateToIso(val)
      }
      // string ISO YYYY-MM-DD or datetime
      if (typeof val === 'string') {
        const isoMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
        // br format dd/mm/yyyy
        const brMatch = val.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
        if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`
      }
      return null
    },

    // recebe ISO (YYYY-MM-DD) e emite conforme emitFormat
    emitFromIso(iso) {
      if (!iso) {
        this.$emit('update:modelValue', null)
        return
      }
      if (this.emitFormat === 'iso') {
        this.$emit('update:modelValue', iso)
        return
      }
      if (this.emitFormat === 'br') {
        this.$emit('update:modelValue', this.isoToBr(iso))
        return
      }
      if (this.emitFormat === 'date') {
        this.$emit('update:modelValue', this.isoToDate(iso))
        return
      }
      // fallback
      this.$emit('update:modelValue', iso)
    },

    // helpers de conversão
    dateToIso(date) {
      if (!(date instanceof Date) || isNaN(date)) return null
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    isoToBr(iso) {
      if (!iso) return ''
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (!m) return iso
      return `${m[3]}/${m[2]}/${m[1]}`
    },
    isoToDate(iso) {
      if (!iso) return null
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (!m) return null
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0)
    },
    normalizeToIso(v) {
      if (v === null || v === undefined || v === '') return null
      if (v instanceof Date) return this.dateToIso(v)
      if (typeof v === 'string') {
        const isoMatch = v.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
        const brMatch = v.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
        if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`
      }
      return null
    }
  }
}
</script>

<style scoped>
.input-group-text i {
  font-size: 1.05rem;
}
</style>

<!-- Exemplo de uso:
input text normal
<InputField label="Nome" v-model="nome" />

password (toggle)
<InputField label="Senha" type="password" v-model="senha" />

file single
<InputField label="Arquivo" type="file" accept="image/*" v-model="meuArquivo" />

file múltiplos
<InputField label="Imagens" type="file" accept="image/*" multiple v-model="imagens" />

date (emite ISO "YYYY-MM-DD")
<InputField label="Data" type="date" v-model="dataIso" />

date (emite BR "DD/MM/YYYY")
<InputField label="Nascimento" type="date" emitFormat="br" v-model="nascimento" :min="'1900-01-01'" />

date com clear
<InputField label="Data" type="date" clearable v-model="data" helpText="Clique no X para limpar" /> -->
